import { FormSchema } from '../../../types/formSchema';
import {
  createParentSection,
  createLeafSection,
  createParentSectionWithFilters,
} from '../../../utils/documentSectionHelpers';
import {
  ACTUALS_LABEL,
  ACTUALS_VALUE,
  USD_CONSTANT_LABEL,
  USD_CONSTANT_VALUE,
  USD_NOMINAL_LABEL,
  USD_NOMINAL_VALUE,
  PERIOD_TREE_DATA,
  DEFAULT_PERIOD,
  STEP_IDS,
} from '../../../constants/mfr-constants';
import {
  PL_DRILL_DOWN_OPTIONS,
  PL_SLICE_BASE_OPTIONS,
  getPLSliceOptions,
} from 'src/config/mfr-workflow-config';
import { isFeatureEnabled } from '../../../config/feature-flags';
import { getMarketplaceTreeData } from '../../../constants/marketplace-constants';
export type { MarketplaceNode } from '../../../constants/marketplace-constants';

const getCurrencyOptions = () => {
  const options = [{ value: USD_CONSTANT_VALUE, label: USD_CONSTANT_LABEL }];
  if (isFeatureEnabled('USD_NOMINAL')) {
    options.push({ value: USD_NOMINAL_VALUE, label: USD_NOMINAL_LABEL });
  }
  return options;
};

export const getMfrFormSchema = (): FormSchema => ({
  id: 'mfr-create',
  title: 'Create MFR Report',
  description:
    'Configure your MFR report with the required filters and document sections.',
  sections: [
    {
      title: 'Basic Information',
      description:
        'Configure the basic information to start your report generation',
      layout: 'single-column',
      fields: [
        {
          type: 'text',
          name: 'name',
          label: 'Name',
          placeholder: 'Enter name...',
          validation: [{ required: true, message: 'Name is required' }],
          componentProps: {
            allowClear: true,
            showCount: true,
            maxLength: 100,
          },
        },
        {
          type: 'textarea',
          name: 'description',
          label: 'Description',
          placeholder: 'Enter description...',
          componentProps: {
            allowClear: true,
            showCount: true,
            maxLength: 500,
            autoSize: { minRows: 3, maxRows: 6 },
          },
        },
      ],
    },
    {
      title: 'Filters',
      layout: 'two-column',
      fields: [
        {
          type: 'treeSelect',
          name: 'marketplace',
          label: 'Marketplace',
          placeholder: 'Select marketplace...',
          multiple: true,
          maxTagCount: 2,
          treeDefaultExpandAll: false,
          treeData: getMarketplaceTreeData(),
          validation: [{ required: true, message: 'Marketplace is required' }],
        },
        {
          type: 'multiselect',
          name: 'channel',
          label: 'Channel',
          placeholder: 'Select channels...',
          options: [
            { value: 'FBA', label: 'FBA' },
            { value: 'MFN', label: 'MFN' },
            { value: 'Retail', label: 'Retail' },
          ],
          validation: [{ required: true, message: 'Channel is required' }],
        },
        {
          type: 'treeSelect',
          name: 'product_lines',
          label: 'Product Line',
          placeholder: 'Select product line...',
          treeDefaultExpandAll: false,
          showSearch: true,
          allowClear: true,
          loadData: true,
          filterType: 'productid',
          treeData: [
            {
              value: 'A1PTOT',
              title: 'A1PTOT - Total Products Alternate',
              key: 'A1PTOT',
              hierarchyType: 'COGNOS_PRODUCT_ALTERNATE_HIERARCHY',
              isRootNode: true,
              isLeaf: false,
            },
            {
              value: 'PTOT',
              title: 'PTOT - Total Products',
              key: 'PTOT',
              hierarchyType: 'COGNOS_PRODUCT_HIERARCHY',
              isRootNode: true,
              isLeaf: false,
            },
            {
              value: 'altProduct',
              title: 'altProduct',
              key: 'altProduct',
              hierarchyType: 'COGNOS_PRODUCT_ALTERNATE_ALT_HIERARCHY',
              isRootNode: true,
              isLeaf: false,
            },
          ],
          validation: [{ required: true, message: 'Product Line is required' }],
        },
        {
          type: 'treeSelect',
          name: 'period',
          label: 'Period',
          placeholder: 'Select period...',
          treeDefaultExpandAll: false,
          treeData: PERIOD_TREE_DATA,
          defaultValue: DEFAULT_PERIOD,
          validation: [{ required: true, message: 'Period is required' }],
        },
        {
          type: 'dropdown',
          name: 'base_scenario',
          label: 'Base Scenario',
          defaultValue: ACTUALS_VALUE,
          readonly: true,
          options: [{ value: ACTUALS_VALUE, label: ACTUALS_LABEL }],
          validation: [
            { required: true, message: 'Base Scenario is required' },
          ],
        },
        {
          type: 'multiselect',
          name: 'comparison_scenario',
          label: 'Comparison Scenario',
          placeholder: 'Select comparison scenario...',
          maxTagCount: 2,
          options: [],
          validation: [
            { required: true, message: 'Comparison Scenario is required' },
          ],
        },
        {
          type: 'dropdown',
          name: 'currency',
          label: 'Currency',
          defaultValue: USD_CONSTANT_VALUE,
          options: getCurrencyOptions(),
          warningMessage: {
            condition: { value: USD_NOMINAL_VALUE },
            text: "Currency filter doesn't apply to Business Trends. This section is based on transaction data and always reports in USD Constant.",
          },
          validation: [{ required: true, message: 'Currency is required' }],
        },
        {
          type: 'dropdown',
          name: 'data_source',
          label: 'Data Source',
          defaultValue: 'Inc Ads',
          options: [
            { value: 'Inc Ads', label: 'Including Advertising' },
            { value: 'Ex Ads', label: 'Excluding Advertising' },
          ],
          warningMessage: {
            condition: { value: 'Ex Ads' },
            text: "Data Source filter doesn't apply to Business Trends. This section is based on transaction data and always includes Advertising.",
          },
          validation: [{ required: true, message: 'Data Source is required' }],
        },
      ],
    },
    {
      title: 'Document Sections',
      layout: 'single-column',
      fields: [
        {
          type: 'documentSectionsTable',
          name: 'documentSections',
          label: '',
          tableData: [
            // Executive P&L Group
            createParentSection('exec-pl', 'Executive P&L', [
              createLeafSection(
                STEP_IDS.EXECUTIVE_PNL_EXECUTIVE_SUMMARY,
                'Executive Summary',
              ),
              createLeafSection(
                STEP_IDS.EXECUTIVE_PNL_PNL_BY_CHANNEL,
                'P&L by Channel',
              ),
              createLeafSection(STEP_IDS.EXECUTIVE_PNL_PNL_BY_GL, 'P&L by GL', {
                hasFilter: true,
                filterType: 'multiselect',
                filterDependsOn: 'productLine',
                filterPlaceholder: 'Select Product Line',
                filterOptions: [],
                maxCount: 12,
              }),
              createLeafSection(
                STEP_IDS.EXECUTIVE_PNL_PNL_BY_MARKETPLACE,
                'P&L by Marketplace',
              ),
            ]),

            // P&L by Slice Group
            createParentSectionWithFilters('pl-slice', 'P&L by Slice', {
              filterType: 'multiselect',
              filterPlaceholder: 'Add Slice',
              filterOptions: PL_SLICE_BASE_OPTIONS,
            }),

            // Business Trends Group
            createParentSection('business-trends', 'Business Trends', [
              createLeafSection(
                STEP_IDS.BUSINESS_TRENDS_TOP_BOTTOM_BRANDS,
                'Top and Bottom 10 Brands',
              ),
              createLeafSection(
                STEP_IDS.BUSINESS_TRENDS_TOP_BOTTOM_ASINS,
                'Top and Bottom 10 ASINs',
              ),
            ]),

            // Cost to Serve (standalone)
            createLeafSection(STEP_IDS.COST_TO_SERVE, 'Cost to Serve'),

            // Detailed P&L (standalone)
            createLeafSection(STEP_IDS.DETAILED_PNL, 'Detailed P&L'),

            // P&L Drill Down Group
            createParentSectionWithFilters('pl-drill-down', 'P&L Drill Down', {
              filterType: 'multiselect',
              filterPlaceholder: 'Add Pnl Line',
              filterOptions: PL_DRILL_DOWN_OPTIONS,
            }),
          ],
        },
      ],
    },
  ],
});

/** @deprecated Use getMfrFormSchema() instead */
export const mfrFormSchema = getMfrFormSchema();
